<template>
  <div class="column content-box">
    <woot-button
      color-scheme="success"
      class-names="button--fixed-right-top"
      icon="arrow-download"
      @click="downloadReports"
    >
      {{ downloadButtonLabel }}
    </woot-button>
    <report-filters
      v-if="filterItemsList.length"
      :type="type"
      :filter-items-list="filterItemsList"
      @date-range-change="onDateRangeChange"
      @filter-change="onFilterChange"
    />
    <div>
      <div v-if="filterItemsList.length" class="row">
        <woot-report-stats-card
          v-for="(metric, index) in metrics"
          :key="metric.KEY"
          :desc="metric.DESC"
          :heading="metric.NAME"
          :index="index"
          :on-click="changeSelection"
          :point="getPoint(metric.KEY)"
          :selected="index === currentSelection"
        />
      </div>
      <div class="report-bar">
        <woot-loading-state
          v-if="accountReport.isFetching || loading"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <div v-else class="chart-and-table-container">
          <div v-if="shouldRenderTable()" class="table-container">
            <template v-if="tableData.length">
              <ReportTable
                :tableData="tableData"
                :selectedFilter="selectedFilter"
                :isQuerySuccess="isQuerySuccess"
              />
            </template>
            <template v-else class="empty-state">
              {{ $t('REPORT.NO_ENOUGH_DATA') }}
            </template>
          </div>
          <div v-else class="chart-container">
            <template v-if="accountReport.data.length && filterItemsList.length">
              <woot-bar :collection="collection" />
            </template>
            <template v-else class="empty-state">
              {{ $t('REPORT.NO_ENOUGH_DATA') }}
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportFilters from './ReportFilters.vue';
import ReportTable from './ReportTable.vue';
import { fromUnixTime, format, addDays, subDays } from 'date-fns';
import axios from 'axios';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  RESOLVED: 'resolved',
};

export default {
  components: {
    ReportFilters,
    ReportTable,
  },
  props: {
    type: {
      type: String,
      default: 'account',
    },
    getterKey: {
      type: String,
      default: '',
    },
    actionKey: {
      type: String,
      default: '',
    },
    downloadButtonLabel: {
      type: String,
      default: 'Download Reports',
    },
  },
  data() {
    return {
      from: 0,
      to: 0,
      currentSelection: 0,
      selectedFilter: null,
      tableData: [],
      isQuerySuccess: false,
      resolvedCount: 0,
      loading: false,
      initialFetch: true,
    };
  },
  computed: {
    filterItemsList() {
      return this.$store.getters[this.getterKey] || [];
    },
    accountSummary() {
      const summary = this.$store.getters.getAccountSummary || {};
      summary[REPORTS_KEYS.RESOLVED] = this.resolvedCount;
      return summary;
    },
    accountReport() {
      return this.$store.getters.getAccountReports || { data: [] };
    },
    collection() {
      if (this.accountReport.isFetching) {
        return {};
      }
      if (!this.accountReport.data.length) return {};
      const labels = this.accountReport.data.map(element =>
        format(fromUnixTime(element.timestamp), 'dd/MMM')
      );
      const data = this.accountReport.data.map(element => element.value);
      return {
        labels,
        datasets: [
          {
            label: this.metrics[this.currentSelection].NAME,
            backgroundColor: '#1f93ff',
            data,
          },
        ],
      };
    },
    metrics() {
      let reportKeys = ['CONVERSATIONS'];
      if (this.type !== 'agent') {
        reportKeys.push('INCOMING_MESSAGES');
      }
      reportKeys = [
        ...reportKeys,
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'RESOLVED',
      ];
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: REPORTS_KEYS[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
      }));
    },
    testMetricIndex() {
      return this.metrics.findIndex(metric => metric.KEY === REPORTS_KEYS.RESOLVED);
    }
  },
  mounted() {
    this.$store.dispatch(this.actionKey).then(() => {
      if (this.initialFetch) {
        this.initialFetch = false;
        this.fetchAllData();
      }
    });
  },
  watch: {
    filterItemsList(newVal) {
      if (newVal.length && !this.selectedFilter && this.initialFetch) {
        this.selectedFilter = newVal[0];
        this.initialFetch = false;
      }
    },
    selectedFilter(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchAllData();
      }
    }
  },
  methods: {
    fetchAllData() {
      if (this.selectedFilter) {
        const { from, to } = this;
        this.$store.dispatch('fetchAccountSummary', {
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
        });
        this.fetchResolvedData();
        this.fetchChartData();
      }
    },
    fetchChartData() {
      if (this.currentSelection !== this.testMetricIndex) {
        const { from, to } = this;
        this.$store.dispatch('fetchAccountReport', {
          metric: this.metrics[this.currentSelection].KEY,
          from,
          to,
          type: this.type,
          id: this.selectedFilter.id,
        });
      }
    },
    async fetchResolvedData() {
      try {
        this.loading = true;
        const startDate = format(subDays(fromUnixTime(this.from), 1), 'yyyy-MM-dd HH:mm:ss');
        const endDate = format(addDays(fromUnixTime(this.to), 1), 'yyyy-MM-dd HH:mm:ss');
        const response = await axios.get(`https://uat-linebc.pugs.team/api/query?name=${this.selectedFilter.name}&startDate=${startDate}&endDate=${endDate}`);
        this.tableData = response.data;
        this.isQuerySuccess = this.tableData.length > 0;
        this.resolvedCount = this.tableData.length;
      } catch (error) {
        this.isQuerySuccess = false;
      } finally {
        this.loading = false;
      }
    },
    getPoint(key) {
      return key === REPORTS_KEYS.RESOLVED ? this.tableData.length : this.accountSummary[key];
    },
    onDateRangeChange({ from, to }) {
      this.from = from;
      this.to = to;
      this.fetchAllData();
    },
    onFilterChange(payload) {
      this.selectedFilter = payload;
    },
    downloadReports() {
      const { from, to } = this;
      const fileName = `${this.type}-report-${format(
        fromUnixTime(to),
        'dd-MM-yyyy'
      )}.csv`;
      switch (this.type) {
        case 'agent':
          this.$store.dispatch('downloadAgentReports', { from, to, fileName });
          break;
        case 'label':
          this.$store.dispatch('downloadLabelReports', { from, to, fileName });
          break;
        case 'inbox':
          this.$store.dispatch('downloadInboxReports', { from, to, fileName });
          break;
        case 'team':
          this.$store.dispatch('downloadTeamReports', { from, to, fileName });
          break;
        default:
          break;
      }
    },
    changeSelection(index) {
      this.currentSelection = index;
      if (this.currentSelection === this.testMetricIndex) {
        this.fetchResolvedData();
      } else {
        this.fetchChartData();
      }
    },
    shouldRenderTable() {
      return this.currentSelection === this.testMetricIndex;
    },
  },
};
</script>

<style scoped>
.data-container {
  display: flex;
  flex-direction: column;
  padding: 10px;
  white-space: pre-wrap;
}
.empty-state {
  padding: 20px;
  text-align: center;
  color: #999;
}
.chart-and-table-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.chart-container {
  padding: 20px;
}
.table-container {
  padding: 20px;
}
</style>